import { createClient } from '@supabase/supabase-js'

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY

// Função para validar se as variáveis de ambiente estão configuradas
export const isSupabaseConfigured = () => {
  return supabaseUrl && 
         supabaseAnonKey && 
         supabaseUrl !== 'your_supabase_url_here' && 
         supabaseAnonKey !== 'your_supabase_anon_key_here' &&
         supabaseUrl.startsWith('https://')
}

// Sempre exporta supabase, mesmo que seja null quando não configurado
export const supabase = isSupabaseConfigured()
  ? createClient(supabaseUrl, supabaseAnonKey, {
      auth: {
        autoRefreshToken: true,
        persistSession: true,
        detectSessionInUrl: false,
      }
    })
  : null

export type Database = {
  public: {
    Tables: {
      agencies: {
        Row: {
          id: string
          company_name: string
          contact_name: string
          email: string
          phone: string
          address: string | null
          complement: string | null
          city: string | null
          state: string | null
          domain: string
          api_url: string
          smtp_host: string | null
          smtp_email: string | null
          smtp_port: number | null
          smtp_ssl: boolean | null
          smtp_password: string | null
          logo_url: string | null
          logo_url_light: string | null
          is_active: boolean | null
          created_at: string | null
          updated_at: string | null
        }
        Insert: {
          id?: string
          company_name: string
          contact_name: string
          email: string
          phone: string
          address?: string | null
          complement?: string | null
          city?: string | null
          state?: string | null
          domain: string
          api_url: string
          smtp_host?: string | null
          smtp_email?: string | null
          smtp_port?: number | null
          smtp_ssl?: boolean | null
          smtp_password?: string | null
          logo_url?: string | null
          logo_url_light?: string | null
          is_active?: boolean | null
          created_at?: string | null
          updated_at?: string | null
        }
        Update: {
          id?: string
          company_name?: string
          contact_name?: string
          email?: string
          phone?: string
          address?: string | null
          complement?: string | null
          city?: string | null
          state?: string | null
          domain?: string
          api_url?: string
          smtp_host?: string | null
          smtp_email?: string | null
          smtp_port?: number | null
          smtp_ssl?: boolean | null
          smtp_password?: string | null
          logo_url?: string | null
          logo_url_light?: string | null
          is_active?: boolean | null
          created_at?: string | null
          updated_at?: string | null
        }
      }
      accounts: {
        Row: {
          id: string
          agency_id: string
          nome: string
          license_id: string
          email: string
          phone: string | null
          timezone: string
          show_user_name_in_messages: boolean
          is_active: boolean
          created_at: string | null
          updated_at: string | null
        }
        Insert: {
          id?: string
          agency_id: string
          nome: string
          license_id: string
          email: string
          phone?: string | null
          timezone?: string
          show_user_name_in_messages?: boolean
          is_active?: boolean
          created_at?: string | null
          updated_at?: string | null
        }
        Update: {
          nome?: string
          email?: string
          phone?: string | null
          timezone?: string
          show_user_name_in_messages?: boolean
          is_active?: boolean
          updated_at?: string | null
        }
      }
      account_users: {
        Row: {
          id: string
          account_id: string
          auth_user_id: string | null
          name: string
          email: string
          phone: string | null
          can_assign_contacts: boolean
          view_all_contacts: boolean | null
          view_unassigned_contacts: boolean
          profile: string
          role: string | null
          is_active: boolean
          token: string | null
          order_index: number
          created_at: string | null
          updated_at: string | null
        }
        Insert: {
          id?: string
          account_id: string
          auth_user_id?: string | null
          name: string
          email: string
          phone?: string | null
          can_assign_contacts?: boolean
          view_all_contacts?: boolean | null
          view_unassigned_contacts?: boolean
          profile?: string
          role?: string | null
          is_active?: boolean
          token?: string | null
          order_index?: number
          created_at?: string | null
          updated_at?: string | null
        }
        Update: {
          id?: string
          account_id?: string
          auth_user_id?: string | null
          name?: string
          email?: string
          phone?: string | null
          can_assign_contacts?: boolean
          view_all_contacts?: boolean | null
          view_unassigned_contacts?: boolean
          profile?: string
          role?: string | null
          is_active?: boolean
          token?: string | null
          order_index?: number
          created_at?: string | null
          updated_at?: string | null
        }
      }
      account_instances: {
        Row: {
          id: string
          account_id: string
          instance_name: string | null
          instance_id: string | null
          apikey: string | null
          created_at: string | null
          connection_version: number
        }
        Insert: {
          id?: string
          account_id: string
          instance_name?: string | null
          instance_id?: string | null
          apikey?: string | null
          created_at?: string | null
          connection_version?: number
        }
        Update: {
          id?: string
          account_id?: string
          instance_name?: string | null
          instance_id?: string | null
          apikey?: string | null
          created_at?: string | null
          connection_version?: number
        }
      }
      account_contacts: {
        Row: {
          id: string
          account_id: string
          jid: string
          lid?: string | null
          is_group: boolean
          name: string | null
          display_name: string | null
          push_name_last: string | null
          email: string | null
          phone: string | null
          wa_id?: string | null
          account_assistant_id: string | null
          assistant_is_active: boolean | null
          attribution_id: string | null
          custom_fields?: Record<string, any> | null
          tags?: string[] | null
          campaigns?: string[] | null
          follow_up_opt_out?: boolean
          status_chat?: string | null
          last_message_at: string | null
          last_message_id: string | null
          last_read_at: string | null
          profile_picture: string | null
          profile_picture_updated_at: string | null
          ai_processing_locked_until?: string | null
          last_processed_assistant_id?: string | null
          created_at: string | null
          updated_at: string | null
        }
        Insert: {
          id?: string
          account_id: string
          jid: string
          lid?: string | null
          is_group?: boolean
          name?: string | null
          display_name?: string | null
          push_name_last?: string | null
          email?: string | null
          phone?: string | null
          wa_id?: string | null
          account_assistant_id?: string | null
          assistant_is_active?: boolean | null
          attribution_id?: string | null
          custom_fields?: Record<string, any> | null
          tags?: string[] | null
          campaigns?: string[] | null
          follow_up_opt_out?: boolean
          status_chat?: string | null
          last_message_at?: string | null
          last_message_id?: string | null
          last_read_at?: string | null
          profile_picture?: string | null
          profile_picture_updated_at?: string | null
          ai_processing_locked_until?: string | null
          last_processed_assistant_id?: string | null
          created_at?: string | null
          updated_at?: string | null
        }
        Update: {
          id?: string
          account_id?: string
          jid?: string
          lid?: string | null
          is_group?: boolean
          name?: string | null
          display_name?: string | null
          push_name_last?: string | null
          email?: string | null
          phone?: string | null
          wa_id?: string | null
          account_assistant_id?: string | null
          assistant_is_active?: boolean | null
          attribution_id?: string | null
          custom_fields?: Record<string, any> | null
          tags?: string[] | null
          campaigns?: string[] | null
          follow_up_opt_out?: boolean
          status_chat?: string | null
          last_message_at?: string | null
          last_message_id?: string | null
          last_read_at?: string | null
          profile_picture?: string | null
          profile_picture_updated_at?: string | null
          ai_processing_locked_until?: string | null
          last_processed_assistant_id?: string | null
          created_at?: string | null
          updated_at?: string | null
        }
      }
      account_messages: {
        Row: {
          id: number
          account_id: string
          remote_jid: string
          from_jid: string
          from_me: boolean
          is_from_ai: boolean
          wa_message_id: string
          msg_type: string
          text_content: string | null
          status: string | null
          sent_at: string
          received_at: string | null
          media_kind: string | null
          media_url: string | null
          raw_payload: any
          is_followup?: boolean
          marketing_source_app?: string | null
          marketing_source_id?: string | null
          marketing_source_url?: string | null
          marketing_conversion_source?: string | null
          marketing_title?: string | null
          marketing_description?: string | null
          marketing_thumbnail_url?: string | null
          marketing_media_type?: number | null
          marketing_greeting_message?: string | null
          marketing_automated_greeting_shown?: boolean | null
          marketing_conversion_data?: any | null
          marketing_touch_type?: string | null
          marketing_form_responses?: any | null
          marketing_attribution_key?: string | null
        }
        Insert: {
          id?: number
          account_id: string
          remote_jid: string
          from_jid: string
          from_me?: boolean
          is_from_ai?: boolean
          wa_message_id: string
          msg_type: string
          text_content?: string | null
          status?: string | null
          sent_at: string
          received_at?: string | null
          media_kind?: string | null
          media_url?: string | null
          raw_payload: any
          is_followup?: boolean
          marketing_source_app?: string | null
          marketing_source_id?: string | null
          marketing_source_url?: string | null
          marketing_conversion_source?: string | null
          marketing_title?: string | null
          marketing_description?: string | null
          marketing_thumbnail_url?: string | null
          marketing_media_type?: number | null
          marketing_greeting_message?: string | null
          marketing_automated_greeting_shown?: boolean | null
          marketing_conversion_data?: any | null
          marketing_touch_type?: string | null
          marketing_form_responses?: any | null
          marketing_attribution_key?: string | null
        }
        Update: {
          id?: number
          account_id?: string
          remote_jid?: string
          from_jid?: string
          from_me?: boolean
          is_from_ai?: boolean
          wa_message_id?: string
          msg_type?: string
          text_content?: string | null
          status?: string | null
          sent_at?: string
          received_at?: string | null
          media_kind?: string | null
          media_url?: string | null
          raw_payload?: any
          is_followup?: boolean
          marketing_source_app?: string | null
          marketing_source_id?: string | null
          marketing_source_url?: string | null
          marketing_conversion_source?: string | null
          marketing_title?: string | null
          marketing_description?: string | null
          marketing_thumbnail_url?: string | null
          marketing_media_type?: number | null
          marketing_greeting_message?: string | null
          marketing_automated_greeting_shown?: boolean | null
          marketing_conversion_data?: any | null
          marketing_touch_type?: string | null
          marketing_form_responses?: any | null
          marketing_attribution_key?: string | null
        }
      }
      account_assistants: {
        Row: {
          id: string
          account_id: string
          name: string
          settings: string | null
          model: string
          response_time_value: number
          response_time_unit: string
          max_messages: number
          prompt_mode: string
          default_prompt: string | null
          voice_tone: string | null
          main_objective: string | null
          language_rules: string | null
          good_examples: string | null
          restrictions: string | null
          additional_info: string | null
          is_active: boolean
          disable_assistant_on_manual_message: boolean
          created_at: string | null
          updated_at: string | null
        }
        Insert: {
          id?: string
          account_id: string
          name: string
          settings?: string | null
          model?: string
          response_time_value?: number
          response_time_unit?: string
          max_messages?: number
          prompt_mode?: string
          default_prompt?: string | null
          voice_tone?: string | null
          main_objective?: string | null
          language_rules?: string | null
          good_examples?: string | null
          restrictions?: string | null
          additional_info?: string | null
          is_active?: boolean
          disable_assistant_on_manual_message?: boolean
          created_at?: string | null
          updated_at?: string | null
        }
        Update: {
          id?: string
          account_id?: string
          name?: string
          settings?: string | null
          model?: string
          response_time_value?: number
          response_time_unit?: string
          max_messages?: number
          prompt_mode?: string
          default_prompt?: string | null
          voice_tone?: string | null
          main_objective?: string | null
          language_rules?: string | null
          good_examples?: string | null
          restrictions?: string | null
          additional_info?: string | null
          is_active?: boolean
          disable_assistant_on_manual_message?: boolean
          created_at?: string | null
          updated_at?: string | null
        }
      }
      account_assistant_webhooks: {
        Row: {
          id: string
          account_id: string
          assistant_id: string
          name: string
          trigger: string
          http_method: string
          url: string
          is_active: boolean
          created_at: string | null
          updated_at: string | null
        }
        Insert: {
          id?: string
          account_id: string
          assistant_id: string
          name: string
          trigger: string
          http_method: string
          url: string
          is_active?: boolean
          created_at?: string | null
          updated_at?: string | null
        }
        Update: {
          id?: string
          account_id?: string
          assistant_id?: string
          name?: string
          trigger?: string
          http_method?: string
          url?: string
          is_active?: boolean
          created_at?: string | null
          updated_at?: string | null
        }
      }
      account_assistant_follow_ups: {
        Row: {
          id: string
          account_id: string
          assistant_id: string
          name: string
          window_start: string
          window_end: string
          days_of_week: string[]
          is_active: boolean
          goal_pipeline_stage_id: string | null
          goal_tags: string[]
          stop_on_human: boolean
          stop_on_won_lost: boolean
          exclusion_tags: string[]
          max_cycles: number
          cooldown_days: number
          active_from: string
          stale_after_hours: number
          created_at: string
          updated_at: string
        }
        Insert: {
          id?: string
          account_id: string
          assistant_id: string
          name: string
          window_start?: string
          window_end?: string
          days_of_week?: string[]
          is_active?: boolean
          goal_pipeline_stage_id?: string | null
          goal_tags?: string[]
          stop_on_human?: boolean
          stop_on_won_lost?: boolean
          exclusion_tags?: string[]
          max_cycles?: number
          cooldown_days?: number
          active_from?: string
          stale_after_hours?: number
          created_at?: string
          updated_at?: string
        }
        Update: {
          id?: string
          account_id?: string
          assistant_id?: string
          name?: string
          window_start?: string
          window_end?: string
          days_of_week?: string[]
          is_active?: boolean
          goal_pipeline_stage_id?: string | null
          goal_tags?: string[]
          stop_on_human?: boolean
          stop_on_won_lost?: boolean
          exclusion_tags?: string[]
          max_cycles?: number
          cooldown_days?: number
          active_from?: string
          stale_after_hours?: number
          created_at?: string
          updated_at?: string
        }
      }
      account_assistant_follow_up_intervals: {
        Row: {
          id: string
          follow_up_id: string
          delay_value: number
          delay_unit: 'minutes' | 'hours' | 'days'
          template_id: string | null
          fluxo_id: string | null
          sequence_order: number
          created_at: string
          updated_at: string
        }
        Insert: {
          id?: string
          follow_up_id: string
          delay_value: number
          delay_unit: 'minutes' | 'hours' | 'days'
          template_id?: string | null
          fluxo_id?: string | null
          sequence_order?: number
          created_at?: string
          updated_at?: string
        }
        Update: {
          id?: string
          follow_up_id?: string
          delay_value?: number
          delay_unit?: 'minutes' | 'hours' | 'days'
          template_id?: string | null
          fluxo_id?: string | null
          sequence_order?: number
          created_at?: string
          updated_at?: string
        }
      }
      account_contact_follow_up_states: {
        Row: {
          id: string
          account_id: string
          account_contact_id: string
          assistant_id: string
          follow_up_id: string
          trigger_message_id: number | null
          trigger_message_at: string | null
          next_interval_order: number
          next_fire_at: string | null
          next_fire_reason: string | null
          dispatch_locked_until: string | null
          last_scheduled_message_id: string | null
          last_error: string | null
          status: 'active' | 'completed' | 'paused' | 'blocked' | 'goal_met' | 'stopped'
          cycle_count: number
          cycle_cooldown_until: string | null
          stop_reason: string | null
          created_at: string
          updated_at: string
        }
        Insert: {
          id?: string
          account_id: string
          account_contact_id: string
          assistant_id: string
          follow_up_id: string
          trigger_message_id?: number | null
          trigger_message_at?: string | null
          next_interval_order?: number
          next_fire_at?: string | null
          next_fire_reason?: string | null
          dispatch_locked_until?: string | null
          last_scheduled_message_id?: string | null
          last_error?: string | null
          status?: 'active' | 'completed' | 'paused' | 'blocked' | 'goal_met' | 'stopped'
          cycle_count?: number
          cycle_cooldown_until?: string | null
          stop_reason?: string | null
          created_at?: string
          updated_at?: string
        }
        Update: {
          id?: string
          account_id?: string
          account_contact_id?: string
          assistant_id?: string
          follow_up_id?: string
          trigger_message_id?: number | null
          trigger_message_at?: string | null
          next_interval_order?: number
          next_fire_at?: string | null
          next_fire_reason?: string | null
          dispatch_locked_until?: string | null
          last_scheduled_message_id?: string | null
          last_error?: string | null
          status?: 'active' | 'completed' | 'paused' | 'blocked' | 'goal_met' | 'stopped'
          cycle_count?: number
          cycle_cooldown_until?: string | null
          stop_reason?: string | null
          created_at?: string
          updated_at?: string
        }
      }
      account_integrations: {
        Row: {
          id: string
          account_id: string
          token_openai: string | null
          created_at: string | null
          updated_at: string | null
        }
        Insert: {
          id?: string
          account_id: string
          token_openai?: string | null
          created_at?: string | null
          updated_at?: string | null
        }
        Update: {
          id?: string
          account_id?: string
          token_openai?: string | null
          created_at?: string | null
          updated_at?: string | null
        }
      }
      account_campaigns: {
        Row: {
          id: string
          account_id: string
          name: string
          assistant_id: string | null
          trigger_type: string
          trigger_keywords: string[] | null
          tags: string[] | null
          pipeline_id: string | null
          pipeline_stage_id: string | null
          is_active: boolean | null
          distribution_member_ids: string[] | null
          last_distribution_index: number | null
          created_at: string | null
          updated_at: string | null
        }
        Insert: {
          id?: string
          account_id: string
          name: string
          assistant_id?: string | null
          trigger_type: string
          trigger_keywords?: string[] | null
          tags?: string[] | null
          pipeline_id?: string | null
          pipeline_stage_id?: string | null
          is_active?: boolean | null
          distribution_member_ids?: string[] | null
          last_distribution_index?: number | null
          created_at?: string | null
          updated_at?: string | null
        }
        Update: {
          id?: string
          account_id?: string
          name?: string
          assistant_id?: string | null
          trigger_type?: string
          trigger_keywords?: string[] | null
          tags?: string[] | null
          pipeline_id?: string | null
          pipeline_stage_id?: string | null
          is_active?: boolean | null
          distribution_member_ids?: string[] | null
          last_distribution_index?: number | null
          created_at?: string | null
          updated_at?: string | null
        }
      }
      account_pipelines: {
        Row: {
          id: string
          account_id: string
          name: string
          description: string | null
          is_active: boolean | null
          created_at: string | null
          updated_at: string | null
        }
        Insert: {
          id?: string
          account_id: string
          name: string
          description?: string | null
          is_active?: boolean | null
          created_at?: string | null
          updated_at?: string | null
        }
        Update: {
          id?: string
          account_id?: string
          name?: string
          description?: string | null
          is_active?: boolean | null
          created_at?: string | null
          updated_at?: string | null
        }
      }
      account_pipeline_stages: {
        Row: {
          id: string
          account_id: string
          pipeline_id: string
          name: string
          order_index: number | null
          is_active: boolean | null
          created_at: string | null
          updated_at: string | null
        }
        Insert: {
          id?: string
          account_id: string
          pipeline_id: string
          name: string
          order_index?: number | null
          is_active?: boolean | null
          created_at?: string | null
          updated_at?: string | null
        }
        Update: {
          id?: string
          account_id?: string
          pipeline_id?: string
          name?: string
          order_index?: number | null
          is_active?: boolean | null
          created_at?: string | null
          updated_at?: string | null
        }
      }
      account_templates: {
        Row: {
          id: string
          account_id: string
          name: string
          content: string
          shortcut: string | null
          category: string | null
          is_active: boolean
          created_at: string
          updated_at: string
        }
        Insert: {
          id?: string
          account_id: string
          name: string
          content: string
          shortcut?: string | null
          category?: string | null
          is_active?: boolean
          created_at?: string
          updated_at?: string
        }
        Update: {
          id?: string
          account_id?: string
          name?: string
          content?: string
          shortcut?: string | null
          category?: string | null
          is_active?: boolean
          created_at?: string
          updated_at?: string
        }
      }
      account_fluxos: {
        Row: {
          id: string
          account_id: string
          name: string
          blocks: unknown
          webhook_enabled: boolean
          webhook_token: string | null
          created_at: string
          updated_at: string
        }
        Insert: {
          id?: string
          account_id: string
          name: string
          blocks?: unknown
          webhook_enabled?: boolean
          webhook_token?: string | null
          created_at?: string
          updated_at?: string
        }
        Update: {
          id?: string
          account_id?: string
          name?: string
          blocks?: unknown
          webhook_enabled?: boolean
          webhook_token?: string | null
          created_at?: string
          updated_at?: string
        }
      }
      account_scheduled_messages: {
        Row: {
          id: string
          account_id: string
          account_contact_id: string
          remote_jid: string
          template_id: string | null
          content_snapshot: string | null
          kind: 'template' | 'fluxo'
          fluxo_id: string | null
          blocks_snapshot: unknown | null
          scheduled_at: string
          status: 'pending' | 'processing' | 'sent' | 'failed' | 'cancelled'
          attempts: number
          max_attempts: number
          rendered_text: string | null
          wa_message_id: string | null
          error_message: string | null
          sent_at: string | null
          created_by: string | null
          source: 'manual' | 'followup'
          cancel_on_reply: boolean
          follow_up_id: string | null
          follow_up_interval_id: string | null
          follow_up_state_id: string | null
          created_at: string
          updated_at: string
        }
        Insert: {
          id?: string
          account_id: string
          account_contact_id: string
          remote_jid: string
          template_id?: string | null
          content_snapshot?: string | null
          kind?: 'template' | 'fluxo'
          fluxo_id?: string | null
          blocks_snapshot?: unknown | null
          scheduled_at: string
          status?: 'pending' | 'processing' | 'sent' | 'failed' | 'cancelled'
          attempts?: number
          max_attempts?: number
          rendered_text?: string | null
          wa_message_id?: string | null
          error_message?: string | null
          sent_at?: string | null
          created_by?: string | null
          source?: 'manual' | 'followup'
          cancel_on_reply?: boolean
          follow_up_id?: string | null
          follow_up_interval_id?: string | null
          follow_up_state_id?: string | null
          created_at?: string
          updated_at?: string
        }
        Update: {
          id?: string
          account_id?: string
          account_contact_id?: string
          remote_jid?: string
          template_id?: string | null
          content_snapshot?: string | null
          kind?: 'template' | 'fluxo'
          fluxo_id?: string | null
          blocks_snapshot?: unknown | null
          scheduled_at?: string
          status?: 'pending' | 'processing' | 'sent' | 'failed' | 'cancelled'
          attempts?: number
          max_attempts?: number
          rendered_text?: string | null
          wa_message_id?: string | null
          error_message?: string | null
          sent_at?: string | null
          created_by?: string | null
          source?: 'manual' | 'followup'
          cancel_on_reply?: boolean
          follow_up_id?: string | null
          follow_up_interval_id?: string | null
          follow_up_state_id?: string | null
          created_at?: string
          updated_at?: string
        }
      }
    }
  }
}